import { useAtlasProducts } from "@labeling-tools/api/hooks";
import { Text, Skeleton, Flex } from "@mantine/core";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { EmbeddingSearchResultSchema } from "@labeling-tools/api/embeddington";
import { IconCheck } from "@tabler/icons-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setPlanogramSelectedProductId,
  setSelectedItem,
  setStagedSelectedItem,
} from "@/store/index";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { ProductSchema } from "@labeling-tools/api/atlas";
import { IconParkingCircle } from "@tabler/icons-react";

interface FilteredProductSchema extends ProductSchema {
  result: EmbeddingSearchResultSchema;
}

const SearchResultItem = ({
  atlasProduct,
  result,
  isSelected,
  onSelect,
  mode,
}: {
  atlasProduct: ProductSchema;
  result: EmbeddingSearchResultSchema;
  isSelected: boolean;
  onSelect: (result: EmbeddingSearchResultSchema) => void;
  mode: "admin" | "viewer" | "qa";
}) => {
  const dispatch = useAppDispatch();

  const handlePlanogramSelect = (e: any) => {
    e.stopPropagation();
    dispatch(setPlanogramSelectedProductId(atlasProduct.id || null));
    dispatch(setStagedSelectedItem(result));
  };

  if (!atlasProduct?.id) return null;
  return (
    <div className="w-1/2 lg:w-1/3 pr-4 pb-4">
      <div className="relative w-full">
        <div
          className={`relative w-full ${mode === "viewer" ? "cursor-pointer" : "cursor-default"}`}
          style={{ paddingBottom: "100%" }}
          onClick={() => {
            if (mode === "viewer") {
              onSelect(result);
            }
          }}
        >
          <div
            className={`absolute inset-0 bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 ${
              isSelected ? "border-2 border-blue-500" : "border border-gray-200"
            }`}
          >
            {!atlasProduct || !result ? (
              <Skeleton height="100%" className="rounded-lg" />
            ) : (
              <>
                {!!atlasProduct?.image_url && (
                  <div className="h-full flex items-center justify-center">
                    <LazyLoadImage
                      src={atlasProduct?.image_url}
                      alt={atlasProduct?.description || "Product image"}
                      effect="opacity"
                      threshold={100}
                      className="w-full h-full rounded-lg hover:opacity-90 transition-opacity duration-200 object-contain"
                      wrapperClassName="w-full h-full"
                      placeholder={
                        <Skeleton
                          height="100%"
                          width="100%"
                          className="rounded-lg"
                        />
                      }
                    />
                  </div>
                )}
                {!atlasProduct?.image_url && (
                  <Flex
                    className="p-4 h-full bg-gray-200 hover:bg-gray-300"
                    direction="column"
                    align="center"
                    justify="center"
                    flex={1}
                  >
                    <Text>{atlasProduct?.description}</Text>
                  </Flex>
                )}
              </>
            )}

            {/* planogram select icon */}
            {!!atlasProduct?.attached_planograms &&
              atlasProduct?.attached_planograms?.length > 0 && (
                <div className="absolute top-2 left-2 cursor-pointer">
                  <IconParkingCircle
                    className="text-gray-500"
                    onClick={handlePlanogramSelect}
                  />
                </div>
              )}

            {/* Selection circle overlay */}
            <div className="absolute top-2 right-2">
              <div
                className={`w-6 h-6 rounded-full border-[1px] flex items-center justify-center transition-all duration-200 ${
                  isSelected
                    ? "bg-blue-500 border-blue-500"
                    : "bg-white/80 border-gray-300 hover:border-blue-300"
                }`}
              >
                {isSelected && <IconCheck size={14} className="text-white" />}
              </div>
            </div>
          </div>
        </div>
        {result.type === "PRODUCT_CATALOG_IMAGE" && (
          <Text>{atlasProduct?.description}</Text>
        )}
      </div>
    </div>
  );
};

export const SearchResultsItems = ({
  results,
  selectedItem,
  onSelect,
  mode,
  taskResultEntityId,
}: {
  results: EmbeddingSearchResultSchema[];
  selectedItem: EmbeddingSearchResultSchema | null;
  onSelect: (result: EmbeddingSearchResultSchema) => void;
  mode: "admin" | "viewer" | "qa";
  taskResultEntityId: string | null;
}) => {
  const { products } = useAtlasProducts(results.map((item) => item.entity_id));

  // We need to filter the products to only include one of each chem_id
  // We prioritize the most recent product for each chem_id
  const filteredProducts: FilteredProductSchema[] = [];
  for (let i = 0; i < products.length; i += 1) {
    const product = products[i];
    if (!product) {
      filteredProducts.push({ result: results[i] } as FilteredProductSchema);
      continue;
    }
    const existingProduct = filteredProducts.find(
      (p) => p.metadata?.chem_id === product?.metadata?.chem_id,
    );
    if (!existingProduct) {
      filteredProducts.push({
        ...product,
        result: results[i],
      } as FilteredProductSchema);
    } else {
      const existingProductIndex = filteredProducts.indexOf(existingProduct);
      if (
        new Date(existingProduct.created_at).getTime() <
        new Date(product?.created_at || "").getTime()
      ) {
        filteredProducts[existingProductIndex] = {
          ...product,
          result: results[i],
        } as FilteredProductSchema;
      }
    }
  }

  return (
    <>
      {filteredProducts.map((item, index) => (
        <SearchResultItem
          key={`${item.id}-${index}`}
          atlasProduct={item}
          result={item.result}
          isSelected={
            selectedItem?.entity_id === item.result.entity_id ||
            taskResultEntityId === item.result.entity_id
          }
          onSelect={onSelect}
          mode={mode}
        />
      ))}
    </>
  );
};

export const SearchResultsRow = ({
  results,
  mode,
  task,
}: {
  results: EmbeddingSearchResultSchema[];
  mode: "admin" | "viewer" | "qa";
  task: JobTaskDetailSchema;
}) => {
  const dispatch = useAppDispatch();
  const selectedItem = useAppSelector(
    (state) => state.productAssociation.selectedItem,
  );

  const handleSelect = (result: EmbeddingSearchResultSchema) => {
    dispatch(setSelectedItem(result));
  };

  if (results.length === 0) {
    return null;
  }

  // filter results to only include one of each entity_id
  // prioritize PRODUCT_CATALOG_IMAGE over PRODUCT_CATALOG_TEXT
  const filteredResults: EmbeddingSearchResultSchema[] = [];
  for (let i = 0; i < results.length; i++) {
    const item = results[i];
    const existingItem = filteredResults.find(
      (i) => i.entity_id === item.entity_id,
    );

    // if no existing item, add the new item
    if (!existingItem) {
      filteredResults.push(item);

      // if existing item is PRODUCT_CATALOG_TEXT and new item is PRODUCT_CATALOG_IMAGE, swap them
    } else if (
      existingItem.type === "PRODUCT_CATALOG_TEXT" &&
      item.type === "PRODUCT_CATALOG_IMAGE"
    ) {
      const existingItemIndex = filteredResults.indexOf(existingItem);
      filteredResults[existingItemIndex] = item;
    }
  }

  // Find the index of the slide that matches task.result?.entity_id
  const taskResultEntityId =
    task.result?.entity_id || task.data?.product_association_result?.entity_id;

  return (
    <div className="space-y-3 w-full">
      <div className="flex flex-row flex-wrap">
        <SearchResultsItems
          results={filteredResults}
          selectedItem={selectedItem}
          onSelect={handleSelect}
          mode={mode}
          taskResultEntityId={taskResultEntityId}
        />
        {/* {filteredResults.map((item) => (
          <div key={item.id} className="w-1/2 lg:w-1/3 pr-4 pb-4">
            <SearchResultItem
              result={item}
              isSelected={
                selectedItem?.entity_id === item.entity_id ||
                taskResultEntityId === item.entity_id
              }
              onSelect={handleSelect}
              mode={mode}
            />
          </div>
        ))} */}
      </div>
    </div>
  );
};
